<template>
	<section class="workflow-step" :id="`${stepNumber}-wfstep`">
		<div class="workflow-container">
			<!-- split into slots / custom and default -->
			<StepHeader
				:stepNumber="stepNumber"
				:customStep="isCustomStep"
				:workflow="workflow"
				:stepEnabled="enabledState"
				:isLabNotesActive="labNotesActive"
				:tourActive="tourActive"
				@seeAllProducts="showProductOptions"
			/>
			<transition name="expand" @enter="enter" @after-enter="afterEnter" @leave="leave" key="content2">
				<div v-if="enabledState">
					<CustomStep v-if="isCustomStep" :customContent="workflow.customContent" :name="workflow.name" />
					<Product
						v-else
						v-for="(prod, i) in getSelectedProducts"
						:product="prod"
						:country="country"
						:key="`${prod.name}-${i}`"
						:productsCount="productsCount(prod)"
						:stepName="workflow.name"
						:stepNumber="stepNumber"
						:isLabNotesActive="labNotesActive"
						:tourActive="tourActive"
						:isTabletView="isTabletView"
						@seeAllProducts="showProductOptions(i)"
						v-on="$listeners"
					/>

					<LabNotes
						v-if="labNotesActive"
						v-model="getLabNotes"
						:class="{'mobile-hide': !labNotesActive}"
						:stepNumber="stepNumber"
						:tourActive="tourActive"
						/>
				</div>
			</transition>
			<div v-if="showQSP">
				<Accordion class="qsp-accordion" :label="'Quick-Start Protocols'">
					<QuickStartProtocol
						v-for="(protocol, i) in qspAccordion"
						:key="`${protocol}-${i}`"
						:protocol="protocol"
					/>
				</Accordion>
			</div>
		</div>
		<AddStepBtn @addStep="addWorkflowStep" class="add-workflow-step" />
	</section>
</template>
<script>
import {trackEvent} from '@/services/tracking.js';

import AddStepBtn from './AddStepBtn.vue';
import CustomStep from './CustomStep.vue';
import Product from './Product/Product.vue';
import StepHeader from './StepHeader.vue';
import LabNotes from './LabNotes.vue';
import Accordion from '@/components/Accordion/Accordion.vue';
import QuickStartProtocol from '@/components/QuickStartProtocol/QuickStartProtocol.vue';

export default {
	name: 'WorkflowStep',
	props: {
		workflow: {
			type: Object,
		},
		stepNumber: {
			type: Number,
		},
		isCustomStep: {
			type: Object,
		},
		country: {
			type: String,
			require: true,
			default: 'US',
		},
		numberOfSteps: {
			type: Number,
		},
		labNotesActive: {
			type: Boolean,
			default: false,
		},
		tourActive: {
			type: Boolean,
			default: false
		},
		isTabletView: {
			type: Boolean,
			default: false
		}
	},
	components: {
		AddStepBtn,
		CustomStep,
		LabNotes,
		Product,
		StepHeader,
		Accordion,
		QuickStartProtocol,
	},
	data() {
		return {}
	},
	methods: {
		addWorkflowStep() {
			this.$emit('addStep', this.workflow.name);
		},
		showProductOptions(i) {
			this.$emit('showProductOptions', {
				stepNumber: this.stepNumber,
				...this.workflow,
				productIndex: i,
			});
		},
		enter(element) {
			this.$emit('enterTransition');
			const el = element;
			const width = getComputedStyle(el).width;
			el.style.width = width;
			el.style.position = 'absolute';
			el.style.visibility = 'hidden';
			el.style.height = 'auto';
			const height = getComputedStyle(el).height;
			el.style.width = null;
			el.style.position = null;
			el.style.visibility = null;
			el.style.height = 0;

			requestAnimationFrame(() => {
				el.style.height = height;
			});
		},
		afterEnter(element) {
			this.$emit('afterenterTransition');
			const el = element;
			el.style.height = 'auto';
		},
		leave(element) {
			this.$emit('leaveTransition');
			const el = element;
			const height = getComputedStyle(el).height;
			el.style.height = height;

			requestAnimationFrame(() => {
				el.style.height = 0;
			});
		},
		productsCount(currProd) {
			// adds count for custom-product and selected product for row:
			// 1 if current product is a custom-product or 2 if standard product
			const numOfExtraProducts = currProd.key.includes('custom-product') ? 1 : 2;
			return (
				this.workflow?.products.filter((product) => !product.key.includes('custom-product')).length -
				this.disabledProducts.length -
				this.getSelectedProducts.filter((prod) => !prod.key.includes('custom-product')).length +
				numOfExtraProducts
			);
		},
	},
	computed: {
		enabledState: {
			get() {
				return this.workflow.stepEnabled;
			},
			set(newVal) {
				trackEvent('ctaClick', {eVar58: 'Disable/Enable step | Workflow Configurator'});
				this.workflow.stepEnabled = newVal;
				this.$root.$emit('runExclusiveExcludeRules');
				this.$root.$emit('changeRegistered');
			},
		},
		getLabNotes: {
			get() {
				return this.workflow.labNotes.desc;
			},
			set(newVal) {
				this.$store.dispatch('workflow/setLabNotes', {name: this.workflow.name, desc: newVal});
				this.$root.$emit('changeRegistered');
			},
		},
		getSelectedProducts() {
			const selected = this.workflow.selected;
			return selected.map(({initial}) => this.workflow.products.find((product) => product.key === initial));
		},
		disabledProducts() {
			// gets number of disabled products
			return this.workflow.products.filter((product) => {
				// removes custom products and products without rules
				if (product?.customContent || !product?.rules) return false;

				// keeps excluded products && exclusively removed products that are not currently selected
				return (
					product?.rules.excludeBy.size > 0 ||
					(product?.rules.exclusiveBy.size > 0 &&
						!this.workflow?.selected.some((sel) => sel.initial === product.key))
				);
			});
		},
		showQSP() {
			return this.labNotesActive && this.enabledState ? this.qspAccordion.length : false;
		},
		catalogNumbers() {
			if (this.workflow.customContent) {
				return [];
			}
			let catNums = [];
			this.getSelectedProducts.forEach((selectedProd) => {
				if (selectedProd.catalogNumber) {
					catNums.push(selectedProd.catalogNumber);
				}
			});
			return catNums;
		},
		qspAccordion() {
			let qspAccordion = [];
			let qspIds = [];
			this.catalogNumbers.forEach((catNum) => {
				let qsps = this.$store.getters.getQuickStartProtocol(catNum);
				qsps.forEach((qsp) => {
					// only include unique qsps for the current country
					if (!qsp.hide_countries.includes(this.country) && !qspIds.includes(qsp.protocolId)) {
						qspAccordion.push(qsp);
						qspIds.push(qsp.protocolId);
					}
				});
			});
			return qspAccordion;
		},
	},
};
</script>
<style lang="scss">
.no-click {
	 pointer-events: none;
}
.workflow-step {
	.workflow-container {
		overflow: hidden;
		background-color: $white;
		@include box(100%);
		border-radius: 4px;

		&-body {
			border-top: 1px solid $color-light-grey;
			height: auto;
			border-radius: 0 0 4px 4px;
			padding: 0 16px;
			will-change: height;
			transition: 0.3s;
			@media only screen and (max-width: $mobile) {
				padding: 0px;
			}
			@media only screen and (min-width: $mobile+1) {
				@include flexRow;
			}
		}
		.qsp-accordion {
			padding-bottom: 22px;
			@media only screen and (max-width: $mobile) {
				display: none;
			}
		}
	}

	.expand-enter-active,
	.expand-leave-active {
		transition: height 0.3s ease-in-out;
		overflow: hidden;
	}

	.expand-enter,
	.expand-leave-to {
		height: 0;
	}
	.add-workflow-step {
		text-align: center;
	}
	&:last-of-type {
		.add-workflow-step {
			@media (max-width: $mobile) {
				display: none;
			}
		}
	}
}
</style>
